import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/mockData';
import { Eye, ShoppingCart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const ShopPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  return (
    <main>
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_351ea547-de6d-47c1-9ac1-d9729f5b49eb/artifacts/96u23ywe_IMG_2653.jpeg"
            alt="Fresh mixed grain feed"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#3B2F1E]/90 via-[#3B2F1E]/50 to-[#2D5016]/30" />
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
            Shop Our Feed
          </h1>
          <Link
            to="/contact"
            className="inline-block px-8 py-3 rounded-lg bg-[#A0522D] text-white font-semibold hover:bg-[#8B4513] transition-colors"
          >
            Contact Us
          </Link>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-[#FAF6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-[#2D5016]/10 text-[#2D5016] text-xs font-semibold uppercase tracking-wider mb-4">
              Quality Feed
            </span>
            <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-4">
              Our Products
            </h2>
            <p className="text-[#3B2F1E]/70 max-w-2xl mx-auto">
              All our feed is Non-GMO and made with quality nutrition in mind. Contact us to place your order.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group hover:-translate-y-2"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-[#2D5016]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <button
                      onClick={() => handleViewDetails(product)}
                      className="flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-[#2D5016] font-semibold hover:bg-[#D4A84B] hover:text-[#3B2F1E] transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                      View Details
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-serif text-lg font-bold text-[#3B2F1E] mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-[#3B2F1E]/60 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="space-y-1 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[#3B2F1E]/70 text-sm">{product.bagSize}</span>
                      <span className="font-bold text-xl text-[#2D5016]">
                        ${product.price.toFixed(2)}
                      </span>
                    </div>
                    {product.totePrice && (
                      <div className="flex items-center justify-between">
                        <span className="text-[#3B2F1E]/70 text-sm">{product.toteSize}</span>
                        <span className="font-bold text-lg text-[#2D5016]">
                          ${product.totePrice.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleViewDetails(product)}
                    className="text-[#A0522D] font-medium text-sm hover:underline"
                  >
                    Quick View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Order Info */}
      <section className="py-16 bg-[#2D5016]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Order?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Contact us by phone, email, or visit our location in Kalispell, Montana. We offer local delivery and convenient pickup options.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:406-607-2302"
              className="px-8 py-3 rounded-lg bg-[#D4A84B] text-[#3B2F1E] font-semibold hover:bg-[#c49a3d] transition-colors"
            >
              Call 406-607-2302
            </a>
            <Link
              to="/contact"
              className="px-8 py-3 rounded-lg border-2 border-white/50 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              Contact Page
            </Link>
          </div>
        </div>
      </section>

      {/* Product Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#FAF6F0]">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#3B2F1E]">
                  {selectedProduct.name}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-64 object-cover rounded-xl"
                />
                <div>
                  <p className="text-[#3B2F1E]/70 mb-4">
                    {selectedProduct.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between bg-[#2D5016]/10 rounded-lg p-3">
                      <span className="text-[#3B2F1E] font-medium">{selectedProduct.bagSize}</span>
                      <span className="font-bold text-2xl text-[#2D5016]">
                        ${selectedProduct.price.toFixed(2)}
                      </span>
                    </div>
                    {selectedProduct.totePrice && (
                      <div className="flex items-center justify-between bg-[#D4A84B]/10 rounded-lg p-3">
                        <span className="text-[#3B2F1E] font-medium">{selectedProduct.toteSize}</span>
                        <span className="font-bold text-2xl text-[#2D5016]">
                          ${selectedProduct.totePrice.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-[#3B2F1E]/60 mb-4">
                    Non-GMO • Quality Nutrition
                  </p>
                  <div className="space-y-3">
                    <a
                      href="tel:406-607-2302"
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg bg-[#A0522D] text-white font-semibold hover:bg-[#8B4513] transition-colors"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Call to Order
                    </a>
                    <a
                      href="mailto:Robert@goodgrainsmt.com"
                      className="flex items-center justify-center w-full px-6 py-3 rounded-lg border-2 border-[#2D5016] text-[#2D5016] font-semibold hover:bg-[#2D5016] hover:text-white transition-colors"
                    >
                      Email to Order
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default ShopPage;
